import { useMenu } from '@if/mui'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import DeleteIcon from '@mui/icons-material/Delete'
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove'
import EditIcon from '@mui/icons-material/Edit'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import { IconButton, Menu, MenuItem, Stack, Tooltip } from '@mui/material'
import { useUser } from '@src/providers/UserProvider'
import { ECatchUpLatePaymentMethod } from '@src/types/constants'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetOutstandingBalance } from '../LoanCalendar-hooks'
import { ELoanCalendarAction, useLoanCalendarAction } from '../LoanCalendarActionProvider'
import { canChangePaymentStatus } from '../LoanCalendarPaymentStatus/LoanCalendarPaymentStatus-selectors'
import { useLoanCalendarSimulation } from '../LoanCalendarSimulationProvider'
import { CanSpreadOverdueAmountOnDates } from '../LoanSpreadOverdueAmountDialog/LoanSpreadOverdueAmount-selector'

const LoanCalendarTableSecondaryActions = () => {
  const { t } = useTranslation()
  const user = useUser()
  const canBypassAcpLock = user?.rights.canEditLockedPpaPayments ?? false
  const { currentSimulation } = useLoanCalendarSimulation()
  const { selectedPaymentIds, dispatchAction } = useLoanCalendarAction()
  const hasAmountNotReplaced = useGetOutstandingBalance(currentSimulation.simulation) > 0
  const canChangePaymentsStatus = canChangePaymentStatus(selectedPaymentIds, currentSimulation.simulation)
  const [buttonMagicProps, menuMagicProps, handleMagicMenuClose] = useMenu()

  const handleMagicMenuMethodSelect = (method: ECatchUpLatePaymentMethod) => {
    handleMagicMenuClose()

    switch (method) {
      case ECatchUpLatePaymentMethod.CatchUpOnSinglePayment:
        dispatchAction(ELoanCalendarAction.OpenCatchUpAmountOfOverdueForDate)
        break
      case ECatchUpLatePaymentMethod.CatchUpOnRemainingPayments:
        dispatchAction(ELoanCalendarAction.OpenCatchUpOnRemainingPaymentsDialog)
        break
      case ECatchUpLatePaymentMethod.CatchUpOnNextPayments:
        dispatchAction(ELoanCalendarAction.OpenSpreadOverLateAmountDialog)
        break
      default:
        break
    }
  }

  const paymentDates = useMemo(
    () =>
      currentSimulation.simulation.customerPaymentOperations
        .filter((op) => selectedPaymentIds.has(op.simulatedOperationId))
        .map((p) => p.operation.effectiveDate),
    [currentSimulation, selectedPaymentIds],
  )

  const canSpreadOverPayment = useMemo(
    () => CanSpreadOverdueAmountOnDates(currentSimulation.simulation, paymentDates, canBypassAcpLock),
    [currentSimulation, paymentDates, canBypassAcpLock],
  )
  return (
    <>
      <Stack direction="row" display={{ xs: 'none', md: 'flex' }}>
        <Tooltip title={t('loan.payment.catchUpPayments')}>
          <span>
            <IconButton disabled={!hasAmountNotReplaced} {...buttonMagicProps}>
              <AutoFixHighIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Menu {...menuMagicProps} onClose={handleMagicMenuClose}>
          <MenuItem onClick={() => handleMagicMenuMethodSelect(ECatchUpLatePaymentMethod.CatchUpOnSinglePayment)}>
            {t(`enum.ECatchUpLatePaymentMethod.catchUpOnSinglePayment`)}
          </MenuItem>
          <MenuItem onClick={() => handleMagicMenuMethodSelect(ECatchUpLatePaymentMethod.CatchUpOnRemainingPayments)}>
            {t(`enum.ECatchUpLatePaymentMethod.catchUpOnRemainingPayments`)}
          </MenuItem>
          <MenuItem
            disabled={!canSpreadOverPayment}
            onClick={() => handleMagicMenuMethodSelect(ECatchUpLatePaymentMethod.CatchUpOnNextPayments)}
          >
            {t(`enum.ECatchUpLatePaymentMethod.catchUpOnNextPayments`)}
          </MenuItem>
        </Menu>
        <Tooltip title={t('loan.payment.edit')}>
          <span>
            <IconButton
              disabled={selectedPaymentIds.size === 0}
              onClick={() => dispatchAction(ELoanCalendarAction.OpenEditPaymentsDialog)}
            >
              <EditIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={t('loan.payment.moveSelectedPayments.title')}>
          <span>
            <IconButton
              disabled={selectedPaymentIds.size === 0}
              onClick={() => dispatchAction(ELoanCalendarAction.OpenMoveSelectedPaymentsDialog)}
            >
              <DriveFileMoveIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={t('loan.payment.increase')}>
          <span>
            <IconButton
              disabled={selectedPaymentIds.size === 0}
              onClick={() => {
                dispatchAction(ELoanCalendarAction.OpenIncreasePaymentsDialog)
              }}
            >
              <TrendingUpIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={t('loan.payment.reduce')}>
          <span>
            <IconButton
              disabled={selectedPaymentIds.size === 0}
              onClick={() => {
                dispatchAction(ELoanCalendarAction.OpenDecreasePaymentsDialog)
              }}
            >
              <TrendingDownIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={t('loan.payment.editStatus')}>
          <span>
            <IconButton
              disabled={!canChangePaymentsStatus}
              onClick={() => dispatchAction(ELoanCalendarAction.OpenChangePaymentStatusDialog)}
            >
              <CheckCircleIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={t('loan.payment.deleteAllSelected')} color="error">
          <span>
            <IconButton
              disabled={selectedPaymentIds.size === 0}
              onClick={() => dispatchAction(ELoanCalendarAction.OpenDeletePaymentsDialog)}
            >
              <DeleteIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={t('loan.feesDialog.title')}>
          <span>
            <IconButton onClick={() => dispatchAction(ELoanCalendarAction.OpenChangeFeeAmountsDialog)}>
              <AttachMoneyIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
      <IconButton sx={{ display: { md: 'none' } }}>
        <MoreVertIcon />
      </IconButton>
    </>
  )
}
export default LoanCalendarTableSecondaryActions
